/**
 * Runtime audio mixer.
 *
 * Sound definitions deliberately describe offline, mono synthesis. This class is
 * the scene-facing half of that contract: it owns the live context, turns each
 * definition into a cached buffer at the device's actual sample rate, then adds
 * buses, positioning and lifecycle rules without leaking rendering-library types
 * into gameplay code.
 */

import { getSound } from './registry';
import { mulberry32 } from './prng';
import { softClip } from './synth';
import type { SoundCategory, SoundDefinition } from './types';

/** A renderer-independent point or direction in the game's metre-based world. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Options applied when a sound instance is started. */
export interface PlayOptions {
  /** World position in metres. Omit this for a non-spatial UI or global sound. */
  position?: Vec3;
  /** Linear source gain. Values below zero are treated as silence. */
  gain?: number;
  /** Playback speed. Non-positive or non-finite values fall back to normal speed. */
  playbackRate?: number;
}

/** Extra one-shot variation. `detune` is a symmetric random range in cents. */
export interface OneShotOptions extends PlayOptions {
  /**
   * Random detune range in cents. For example, `20` picks one value from
   * -20..20 cents for this play; it is not a fixed 20-cent transposition.
   */
  detune?: number;
}

/** A live voice, or an inert no-op voice when audio is unavailable. */
export interface AudioHandle {
  setPosition(v: Vec3): void;
  setGain(value: number, rampSeconds?: number): void;
  setPlaybackRate(rate: number, rampSeconds?: number): void;
  stop(fadeSeconds?: number): void;
  readonly active: boolean;
}

/** Construction policy for the runtime mixer. */
export interface AudioEngineOptions {
  /** Maximum simultaneously active one-shots for a single id. Defaults to eight. */
  maxOneShotsPerId?: number;
  /**
   * Suspend while the document is hidden and resume when it becomes visible.
   * Off by default: games that own a broader pause policy can opt in explicitly.
   */
  observeVisibility?: boolean;
  /** Initial linear master gain, before the final soft clip. Defaults to 0.8. */
  masterGain?: number;
}

interface ModernListenerParams {
  readonly positionX: AudioParam;
  readonly positionY: AudioParam;
  readonly positionZ: AudioParam;
  readonly forwardX: AudioParam;
  readonly forwardY: AudioParam;
  readonly forwardZ: AudioParam;
  readonly upX: AudioParam;
  readonly upY: AudioParam;
  readonly upZ: AudioParam;
}

/** Deprecated listener methods retained by older WebKit implementations. */
interface LegacyListenerMethods {
  setPosition(x: number, y: number, z: number): void;
  setOrientation(
    forwardX: number,
    forwardY: number,
    forwardZ: number,
    upX: number,
    upY: number,
    upZ: number,
  ): void;
}

interface VoiceNodes {
  source: AudioBufferSourceNode;
  gain: GainNode;
  panner?: PannerNode;
}

const CATEGORIES: readonly SoundCategory[] = ['machine', 'foley', 'creature', 'music', 'ui'];
const DEFAULT_ONE_SHOT_CAP = 8;
const DEFAULT_DUCK_ATTACK_SECONDS = 0.08;
const DEFAULT_DUCK_RELEASE_SECONDS = 0.35;
const DEFAULT_STOP_FADE_SECONDS = 0.03;
const DETUNE_RANDOM_SEED = 0x524f5341;

function finite(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function nonNegative(value: number, fallback: number): number {
  return Math.max(0, finite(value, fallback));
}

function positive(value: number, fallback: number): number {
  const safe = finite(value, fallback);
  return safe > 0 ? safe : fallback;
}

function hasModernListenerParams(listener: AudioListener): listener is AudioListener & ModernListenerParams {
  return (
    listener.positionX !== undefined &&
    listener.positionY !== undefined &&
    listener.positionZ !== undefined &&
    listener.forwardX !== undefined &&
    listener.forwardY !== undefined &&
    listener.forwardZ !== undefined &&
    listener.upX !== undefined &&
    listener.upY !== undefined &&
    listener.upZ !== undefined
  );
}

/** A stable, inert handle used for pre-unlock, render-pending and failed audio. */
class SilentHandle implements AudioHandle {
  readonly active = false;

  setPosition(): void {}

  setGain(): void {}

  setPlaybackRate(): void {}

  stop(): void {}
}

/** One source plus its source-local controls and deterministic cleanup. */
class Voice implements AudioHandle {
  private stopped = false;
  /** A scheduled fade still counts as active, but must never be scheduled twice. */
  private stopping = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly nodes: VoiceNodes,
    private readonly onFinished: () => void,
    private readonly onError: (prefix: string, error: unknown) => void,
  ) {
    nodes.source.onended = () => this.finish();
  }

  get active(): boolean {
    return !this.stopped;
  }

  setPosition(v: Vec3): void {
    const panner = this.nodes.panner;
    if (!panner || this.stopped) return;
    try {
      const now = this.ctx.currentTime;
      panner.positionX.setValueAtTime(finite(v.x, 0), now);
      panner.positionY.setValueAtTime(finite(v.y, 0), now);
      panner.positionZ.setValueAtTime(finite(v.z, 0), now);
    } catch (error: unknown) {
      this.onError('could not set sound position', error);
    }
  }

  setGain(value: number, rampSeconds = 0): void {
    if (this.stopped) return;
    try {
      const now = this.ctx.currentTime;
      const target = nonNegative(value, 1);
      const ramp = nonNegative(rampSeconds, 0);
      this.nodes.gain.gain.cancelScheduledValues(now);
      this.nodes.gain.gain.setValueAtTime(this.nodes.gain.gain.value, now);
      if (ramp > 0) this.nodes.gain.gain.linearRampToValueAtTime(target, now + ramp);
      else this.nodes.gain.gain.setValueAtTime(target, now);
    } catch (error: unknown) {
      this.onError('could not set sound gain', error);
    }
  }

  setPlaybackRate(rate: number, rampSeconds = 0): void {
    if (this.stopped) return;
    try {
      const now = this.ctx.currentTime;
      const target = positive(rate, 1);
      const ramp = nonNegative(rampSeconds, 0);
      this.nodes.source.playbackRate.cancelScheduledValues(now);
      this.nodes.source.playbackRate.setValueAtTime(this.nodes.source.playbackRate.value, now);
      if (ramp > 0) this.nodes.source.playbackRate.linearRampToValueAtTime(target, now + ramp);
      else this.nodes.source.playbackRate.setValueAtTime(target, now);
    } catch (error: unknown) {
      this.onError('could not set sound playback rate', error);
    }
  }

  stop(fadeSeconds = DEFAULT_STOP_FADE_SECONDS): void {
    if (this.stopped || this.stopping) return;
    this.stopping = true;
    try {
      const now = this.ctx.currentTime;
      const fade = nonNegative(fadeSeconds, DEFAULT_STOP_FADE_SECONDS);
      this.nodes.gain.gain.cancelScheduledValues(now);
      this.nodes.gain.gain.setValueAtTime(this.nodes.gain.gain.value, now);
      this.nodes.gain.gain.linearRampToValueAtTime(0, now + fade);
      this.nodes.source.stop(now + fade);
    } catch (error: unknown) {
      this.onError('could not stop sound', error);
      this.finish();
    }
  }

  /** Stop immediately during disposal, without scheduling against a closing context. */
  dispose(): void {
    if (this.stopped) return;
    try {
      this.nodes.source.stop();
    } catch {
      // A source may already have ended between bookkeeping and disposal.
    }
    this.finish();
  }

  private finish(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.nodes.source.onended = null;
    try {
      this.nodes.source.disconnect();
      this.nodes.gain.disconnect();
      this.nodes.panner?.disconnect();
    } catch {
      // Disconnect is best-effort; cleanup must never make gameplay throw.
    }
    this.onFinished();
  }
}

/**
 * Owns optional Web Audio playback for the game.
 *
 * Calls made before `unlock()` deliberately return an inactive handle. A first
 * request for a sound starts its offline render but is also dropped; this keeps
 * both `playLoop` and `playOneShot` synchronous and avoids a late sound playing
 * after the moment that requested it has passed. Call again once it is cached.
 */
export class AudioEngine {
  /** Last recoverable audio problem. Normal playback never logs to the console. */
  lastError: string | undefined;

  /**
   * Total one-shots actually started. Diagnostics only — but the only way a test
   * can assert that her footfalls fire, since there is no output device to
   * listen to and a silently dropped voice is otherwise indistinguishable from
   * a played one.
   */
  oneShotsStarted = 0;

  /**
   * Per-id one-shot starts. Diagnostics only, and it exists because the total
   * above cannot answer the question a spec actually has.
   *
   * `oneShotsStarted` moves for every footfall, so a test that walks Rosalie to
   * make Holly follow cannot use it to prove Holly's bell played: the delta is
   * dominated by feet. Deleting the bell's `playOneShot` entirely still left
   * `wool-shed-bell.spec.ts` green, which an independent review caught.
   */
  readonly oneShotStartsById = new Map<string, number>();

  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly renders = new Map<string, Promise<AudioBuffer | undefined>>();
  private readonly voices = new Set<Voice>();
  private readonly oneShotsById = new Map<string, Set<Voice>>();
  private readonly maxOneShotsPerId: number;
  private readonly initialMasterGain: number;
  private readonly visibilityListener: (() => void) | undefined;
  private context: AudioContext | undefined;
  private buses = new Map<SoundCategory, GainNode>();
  private master: GainNode | undefined;
  private clippedMaster: WaveShaperNode | undefined;
  private disposed = false;
  private pausedByGame = false;
  private lifecycleTail: Promise<void> = Promise.resolve();
  private readonly detuneRandom = mulberry32(DETUNE_RANDOM_SEED);

  constructor(options: AudioEngineOptions = {}) {
    this.maxOneShotsPerId = Math.max(
      1,
      Math.floor(positive(options.maxOneShotsPerId ?? DEFAULT_ONE_SHOT_CAP, DEFAULT_ONE_SHOT_CAP)),
    );
    this.initialMasterGain = nonNegative(options.masterGain ?? 0.8, 0.8);

    if (options.observeVisibility === true && typeof document !== 'undefined') {
      this.visibilityListener = () => {
        void this.syncSuspension();
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
    }
  }

  /**
   * Create and resume the owned context from a user gesture. It is intentionally
   * the only method that creates a context, so constructing the game is silent.
   */
  async unlock(): Promise<void> {
    if (this.disposed) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    try {
      if (ctx.state !== 'running') await ctx.resume();
    } catch (error: unknown) {
      this.recordError('could not unlock audio', error);
    }
  }

  /**
   * Render sounds into the cache before anything asks to play them.
   *
   * Without this, the first `playOneShot` for an id returns silence: rendering
   * is async, so the request that triggers it cannot also be served by it. Loops
   * start once and would simply be retried, but one-shots are fired from
   * discrete events — her first footfall, her first landing — and a dropped one
   * is not recoverable, it is just missing at the exact moment the player is
   * forming an impression.
   *
   * So the caller declares what it needs at unlock time and waits. Cheap: the
   * whole set renders offline in well under a second.
   */
  async prewarm(ids: readonly string[]): Promise<void> {
    const ctx = this.context;
    if (!ctx || this.disposed) return;
    await Promise.all(
      ids.map(async (id) => {
        const sound = getSound(id);
        if (!sound) {
          this.lastError = `unknown sound "${id}"`;
          return;
        }
        await this.preRender(sound, ctx);
      }),
    );
  }

  /** Start a loop if its pre-rendered buffer is ready; otherwise return silence. */
  playLoop(id: string, options: PlayOptions = {}): AudioHandle {
    return this.play(id, options, true);
  }

  /**
   * Start a one-shot if its pre-rendered buffer is ready; otherwise return silence.
   * The cap drops the newest request once an id has eight active one-shots by
   * default, preserving existing impacts instead of cutting them unnaturally.
   */
  playOneShot(id: string, options: OneShotOptions = {}): AudioHandle {
    return this.play(id, options, false);
  }

  /** Set final mix gain before soft clipping; negative values become silence. */
  setMasterGain(value: number, rampSeconds = 0): void {
    const master = this.master;
    const ctx = this.context;
    if (!master || !ctx || this.disposed) return;
    const now = ctx.currentTime;
    const ramp = nonNegative(rampSeconds, 0);
    const target = nonNegative(value, 1);
    try {
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      if (ramp > 0) master.gain.linearRampToValueAtTime(target, now + ramp);
      else master.gain.setValueAtTime(target, now);
    } catch (error: unknown) {
      this.recordError('could not set master gain', error);
    }
  }

  /**
   * Duck beds beneath Rosalie without flattening the mix. `amount` is 0..1 and
   * affects machine, foley and music only; creature and UI remain intelligible.
   * Defaults (80 ms attack, 350 ms release) are quick enough for breath cues but
   * slow enough not to read as a compressor pumping the farm ambience.
   */
  duck(
    amount: number,
    attackSeconds = DEFAULT_DUCK_ATTACK_SECONDS,
    releaseSeconds = DEFAULT_DUCK_RELEASE_SECONDS,
  ): void {
    const ctx = this.context;
    if (!ctx || this.disposed) return;
    const target = 1 - Math.min(1, nonNegative(amount, 0));
    const timeConstant = target < 1
      ? positive(attackSeconds, DEFAULT_DUCK_ATTACK_SECONDS)
      : positive(releaseSeconds, DEFAULT_DUCK_RELEASE_SECONDS);
    try {
      for (const category of ['machine', 'foley', 'music'] as const) {
        this.buses.get(category)?.gain.setTargetAtTime(target, ctx.currentTime, timeConstant);
      }
    } catch (error: unknown) {
      this.recordError('could not duck audio buses', error);
    }
  }

  /**
   * Set listener transform using modern AudioParams when available, retaining
   * Safari-era `setPosition`/`setOrientation` compatibility as a fallback.
   */
  setListener(position: Vec3, forward: Vec3, up: Vec3): void {
    const ctx = this.context;
    if (!ctx || this.disposed) return;
    const listener = ctx.listener;
    const now = ctx.currentTime;
    try {
      if (hasModernListenerParams(listener)) {
        listener.positionX.setValueAtTime(finite(position.x, 0), now);
        listener.positionY.setValueAtTime(finite(position.y, 0), now);
        listener.positionZ.setValueAtTime(finite(position.z, 0), now);
        listener.forwardX.setValueAtTime(finite(forward.x, 0), now);
        listener.forwardY.setValueAtTime(finite(forward.y, 0), now);
        listener.forwardZ.setValueAtTime(finite(forward.z, -1), now);
        listener.upX.setValueAtTime(finite(up.x, 0), now);
        listener.upY.setValueAtTime(finite(up.y, 1), now);
        listener.upZ.setValueAtTime(finite(up.z, 0), now);
      } else {
        // Modern lib.dom declarations describe both APIs, but old browsers only
        // expose these methods. Keep the compatibility surface local and typed.
        const legacy = listener as AudioListener & LegacyListenerMethods;
        legacy.setPosition(finite(position.x, 0), finite(position.y, 0), finite(position.z, 0));
        legacy.setOrientation(
          finite(forward.x, 0),
          finite(forward.y, 0),
          finite(forward.z, -1),
          finite(up.x, 0),
          finite(up.y, 1),
          finite(up.z, 0),
        );
      }
    } catch (error: unknown) {
      this.recordError('could not set audio listener', error);
    }
  }

  /** Suspend the owned context. Safe to call before unlock or after disposal. */
  async suspend(): Promise<void> {
    const ctx = this.context;
    if (!ctx || this.disposed || ctx.state === 'suspended' || ctx.state === 'closed') return;
    try {
      await ctx.suspend();
    } catch (error: unknown) {
      this.recordError('could not suspend audio', error);
    }
  }

  /** Resume an already unlocked context. This never creates a context by itself. */
  async resume(): Promise<void> {
    const ctx = this.context;
    if (!ctx || this.disposed || ctx.state === 'running' || ctx.state === 'closed') return;
    try {
      await ctx.resume();
    } catch (error: unknown) {
      this.recordError('could not resume audio', error);
    }
  }

  /**
   * Let the game share the same suspension policy as browser backgrounding.
   *
   * A visibility listener that blindly resumes after a tab becomes visible can
   * undo a deliberate pause. Serialising transitions also prevents a quick
   * pause/resume from finishing out of order and leaving the context suspended.
   */
  setPaused(paused: boolean): Promise<void> {
    this.pausedByGame = paused;
    return this.syncSuspension();
  }

  /** Stop voices, detach optional visibility handling and close the owned context. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    for (const voice of [...this.voices]) voice.dispose();
    this.oneShotsById.clear();
    this.buffers.clear();
    this.renders.clear();
    try {
      this.clippedMaster?.disconnect();
      this.master?.disconnect();
      for (const bus of this.buses.values()) bus.disconnect();
      if (this.context && this.context.state !== 'closed') {
        void this.context.close().catch((error: unknown) => {
          this.recordError('could not close audio', error);
        });
      }
    } catch (error: unknown) {
      this.recordError('could not dispose audio', error);
    }
    this.buses.clear();
  }

  private syncSuspension(): Promise<void> {
    this.lifecycleTail = this.lifecycleTail.then(async () => {
      if (this.disposed) return;
      if (this.pausedByGame || (typeof document !== 'undefined' && document.hidden)) {
        await this.suspend();
      } else {
        await this.resume();
      }
    });
    return this.lifecycleTail;
  }

  /**
   * Sound ids this engine refuses to start, whatever asks for it.
   *
   * The runtime mechanism only. **Which** sounds are suppressed is a content
   * judgement and lives in `src/shell/gentle.ts`; D-018 keeps the engine free of
   * the state-to-sound mapping, and "the worst audio" is exactly that kind of
   * knowledge. The engine is told a list and enforces it.
   *
   * Enforced in `play()` rather than by unhooking a caller, because the callers
   * are seven directors and a per-frame mapping layer, and a suppression that
   * has to be remembered at each call site is a suppression that will be
   * forgotten at the eighth.
   */
  private suppressed: ReadonlySet<string> = new Set<string>();

  /** Replace the suppression list. An empty list is the normal state. */
  setSuppressed(ids: readonly string[]): void {
    this.suppressed = new Set(ids);
  }

  /** Whether an id is currently refused. */
  isSuppressed(id: string): boolean {
    return this.suppressed.has(id);
  }

  /**
   * The refused ids. The Gentle Mode gate's seam: a mode whose effect cannot be
   * read back is a mode whose promise to the player nothing can check.
   */
  get suppressedIds(): readonly string[] {
    return [...this.suppressed];
  }

  private play(id: string, options: PlayOptions | OneShotOptions, loop: boolean): AudioHandle {
    if (this.disposed) return new SilentHandle();
    // Suppressed before anything else, including the cap and the buffer check:
    // a suppressed sound must not consume a one-shot slot, must not trigger a
    // render, and must not set `lastError` — it is not a failure, it is a
    // setting the player chose.
    if (this.suppressed.has(id)) return new SilentHandle();
    const ctx = this.context;
    if (!ctx || ctx.state !== 'running') return new SilentHandle();

    const sound = getSound(id);
    if (!sound) {
      this.lastError = `unknown sound "${id}"`;
      return new SilentHandle();
    }
    const buffer = this.buffers.get(id);
    if (!buffer) {
      void this.preRender(sound, ctx);
      return new SilentHandle();
    }

    if (!loop) {
      const active = this.oneShotsById.get(id);
      if (active && active.size >= this.maxOneShotsPerId) {
        this.lastError = `one-shot cap reached for "${id}"`;
        return new SilentHandle();
      }
    }

    try {
      const voice = this.startVoice(id, sound.category, buffer, options, loop);
      if (!loop) {
        this.oneShotsStarted += 1;
        this.oneShotStartsById.set(id, (this.oneShotStartsById.get(id) ?? 0) + 1);
      }
      return voice;
    } catch (error: unknown) {
      this.recordError(`could not play sound "${id}"`, error);
      return new SilentHandle();
    }
  }

  /** `'running'`, `'suspended'`, or `'closed'` — `undefined` before unlock. */
  get contextState(): AudioContextState | undefined {
    return this.context?.state;
  }

  /** Live voices, loops and one-shots together. */
  get activeVoices(): number {
    return this.voices.size;
  }

  /** Sounds rendered into the buffer cache. */
  get cachedSounds(): number {
    return this.buffers.size;
  }

  private ensureContext(): AudioContext | undefined {
    if (this.context) return this.context;
    if (typeof AudioContext === 'undefined') {
      this.lastError = 'Web Audio is unavailable in this environment';
      return undefined;
    }
    try {
      this.context = new AudioContext();
      this.createGraph(this.context);
      return this.context;
    } catch (error: unknown) {
      this.recordError('could not create audio context', error);
      return undefined;
    }
  }

  private createGraph(ctx: AudioContext): void {
    const master = ctx.createGain();
    master.gain.value = this.initialMasterGain;
    const clipper = softClip(ctx, 1.5);
    master.connect(clipper);
    clipper.connect(ctx.destination);
    this.master = master;
    this.clippedMaster = clipper;
    for (const category of CATEGORIES) {
      const bus = ctx.createGain();
      bus.gain.value = 1;
      bus.connect(master);
      this.buses.set(category, bus);
    }
  }

  private async preRender(sound: SoundDefinition, ctx: AudioContext): Promise<AudioBuffer | undefined> {
    const cached = this.buffers.get(sound.id);
    if (cached) return cached;
    const pending = this.renders.get(sound.id);
    if (pending) return pending;

    const render = this.renderSound(sound, ctx.sampleRate)
      .then((buffer) => {
        this.buffers.set(sound.id, buffer);
        return buffer;
      })
      .catch((error: unknown) => {
        this.recordError(`could not render sound "${sound.id}"`, error);
        return undefined;
      })
      .finally(() => {
        this.renders.delete(sound.id);
      });
    this.renders.set(sound.id, render);
    return render;
  }

  private async renderSound(sound: SoundDefinition, sampleRate: number): Promise<AudioBuffer> {
    if (typeof OfflineAudioContext === 'undefined') {
      throw new Error('OfflineAudioContext is unavailable in this environment');
    }
    const frames = Math.max(Math.ceil(sound.duration * sampleRate), 1);
    const offline = new OfflineAudioContext(1, frames, sampleRate);
    sound.render(offline);
    return offline.startRendering();
  }

  private startVoice(
    id: string,
    category: SoundCategory,
    buffer: AudioBuffer,
    options: PlayOptions | OneShotOptions,
    loop: boolean,
  ): AudioHandle {
    const ctx = this.context;
    const bus = this.buses.get(category);
    if (!ctx || !bus) return new SilentHandle();
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    source.buffer = buffer;
    source.loop = loop;
    gain.gain.value = nonNegative(options.gain ?? 1, 1);
    source.playbackRate.value = positive(options.playbackRate ?? 1, 1);
    if (!loop && 'detune' in options && options.detune !== undefined) {
      const range = Math.abs(finite(options.detune, 0));
      source.detune.value = (this.detuneRandom() * 2 - 1) * range;
    }

    let panner: PannerNode | undefined;
    if (options.position) {
      panner = ctx.createPanner();
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'inverse';
      // The farm is built in metres: near detail stays full until 1 m, then
      // falls naturally through a small room and is inaudible beyond 35 m.
      panner.refDistance = 1;
      panner.maxDistance = 35;
      panner.rolloffFactor = 1.2;
      source.connect(gain);
      gain.connect(panner);
      panner.connect(bus);
    } else {
      source.connect(gain);
      gain.connect(bus);
    }

    const nodes: VoiceNodes = panner ? { source, gain, panner } : { source, gain };
    const voice = new Voice(
      ctx,
      nodes,
      () => {
        this.voices.delete(voice);
        this.oneShotsById.get(id)?.delete(voice);
        if (this.oneShotsById.get(id)?.size === 0) this.oneShotsById.delete(id);
      },
      (prefix, error) => this.recordError(prefix, error),
    );
    this.voices.add(voice);
    if (!loop) {
      const voices = this.oneShotsById.get(id) ?? new Set<Voice>();
      voices.add(voice);
      this.oneShotsById.set(id, voices);
    }
    if (options.position) voice.setPosition(options.position);
    try {
      source.start();
    } catch (error: unknown) {
      voice.dispose();
      throw error;
    }
    return voice;
  }

  private recordError(prefix: string, error: unknown): void {
    const detail = error instanceof Error ? error.message : String(error);
    this.lastError = `${prefix}: ${detail}`;
  }
}
